// Aignoz Innovation OS — Persona context, splash, pill switcher, profile (Phase 4).
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket, Microscope, Stethoscope, TrendingUp, Boxes, GraduationCap,
  Building2, User, Check, ChevronDown, ArrowRight, Lock,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PERSONAS, PERSONA_ORDER, computeTRL, bannerCallouts } from "@/personas";
import { useRunsList } from "@/lib";

const ICONS = {
  Rocket, Microscope, Stethoscope, TrendingUp, Boxes,
  GraduationCap, Building2, User,
};

export function PersonaIcon({ name, className = "w-4 h-4" }) {
  const I = ICONS[name] || User;
  return <I className={className} />;
}

// ---------------- Context ----------------
const STORAGE_KEY = "aignoz.persona";
const PersonaCtx = createContext({
  persona: "guest",
  config: PERSONAS.guest,
  setPersona: () => {},
  needsSplash: false,
  reopenSplash: () => {},
});

export function usePersona() { return useContext(PersonaCtx); }

export function PersonaProvider({ children }) {
  const [persona, setPersonaState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
  });
  const [splashOpen, setSplashOpen] = useState(false);

  // Phase 5: legacy first-visit splash is DISABLED — the auth login page owns that flow now.
  // Persona state is still tracked here for existing subscribers.
  useEffect(() => { /* no-op */ }, [persona]);

  const setPersona = useCallback((key) => {
    if (!PERSONAS[key]) return;
    try { localStorage.setItem(STORAGE_KEY, key); } catch { /* ignore quota */ }
    setPersonaState(key);
    setSplashOpen(false);
  }, []);

  const reopenSplash = useCallback(() => setSplashOpen(true), []);

  const config = PERSONAS[persona] || PERSONAS.guest;
  const value = useMemo(() => ({
    persona: persona || "guest",
    config,
    setPersona,
    needsSplash: splashOpen,
    reopenSplash,
  }), [persona, config, splashOpen, setPersona, reopenSplash]);

  return (
    <PersonaCtx.Provider value={value}>
      {children}
      <AnimatePresence>
        {splashOpen && <PersonaSplash onPick={setPersona} canClose={!!persona} onClose={() => setSplashOpen(false)} />}
      </AnimatePresence>
    </PersonaCtx.Provider>
  );
}

// ---------------- Splash ----------------
function PersonaSplash({ onPick, canClose, onClose }) {
  const cards = PERSONA_ORDER.filter((k) => k !== "guest").map((k) => ({ key: k, ...PERSONAS[k] }));
  return (
    <motion.div
      data-testid="persona-splash"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-6"
      style={{ background: "rgba(2, 6, 12, 0.86)", backdropFilter: "blur(18px)" }}
    >
      <motion.div
        initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3, ease: [0.2, 0.7, 0.25, 1] }}
        className="w-full max-w-5xl"
      >
        <div className="flex items-center gap-3 mb-7">
          <img src="/aignoz-logo.png" alt="Aignoz" className="h-10 w-10 object-contain shrink-0" />
          <div>
            <div className="text-base font-semibold tracking-tight">Aignoz Innovation OS</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Healthcare DeepTech Innovation Engine</div>
          </div>
        </div>

        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Who are you?</h1>
        <p className="text-sm text-white/60 mt-2 max-w-2xl">
          Choose your lens. Aignoz adapts to your role in the healthcare innovation ecosystem.
        </p>

        <div className="mt-7 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="persona-cards">
          {cards.map(({ key, name, icon, tagline, accent }) => (
            <motion.button
              key={key}
              data-testid={`persona-card-${key}`}
              onClick={() => onPick(key)}
              whileHover={{ y: -3, borderColor: accent }}
              transition={{ duration: 0.15 }}
              className="text-left rounded-xl border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] p-5 transition group min-w-0"
              style={{ "--accent": accent }}
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg mb-3"
                style={{ background: `rgba(${PERSONAS[key].accentRgb}, 0.10)`, border: `1px solid rgba(${PERSONAS[key].accentRgb}, 0.35)` }}>
                <PersonaIcon name={icon} className="w-5 h-5" style={{ color: accent }} />
              </div>
              <div className="text-base font-semibold tracking-tight">{name}</div>
              <div className="text-xs text-white/55 mt-1 leading-relaxed">{tagline}</div>
            </motion.button>
          ))}
        </div>

        <div className="mt-7 flex items-center justify-between">
          <button
            onClick={() => onPick("guest")}
            data-testid="persona-guest"
            className="text-xs text-white/50 hover:text-teal-300 transition inline-flex items-center gap-1"
          >
            Continue as Guest <ArrowRight className="w-3 h-3" />
          </button>
          {canClose && (
            <button onClick={onClose} data-testid="persona-splash-close"
              className="text-xs text-white/40 hover:text-white/70 transition">
              Close
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ---------------- Persona Pill (header) ----------------
export function PersonaPill({ compact = false }) {
  const { persona, config, setPersona, reopenSplash } = usePersona();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="persona-pill"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] transition hover:bg-white/[0.04]"
          style={{
            borderColor: `rgba(${config.accentRgb}, 0.45)`,
            background: `rgba(${config.accentRgb}, 0.08)`,
            color: `rgba(${config.accentRgb}, 1)`,
          }}
        >
          <PersonaIcon name={config.icon} className="w-3 h-3" />
          <span className="font-semibold tracking-tight">{config.name}</span>
          <span
            aria-hidden="true"
            className="ml-0.5 w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: "#D17EC0", boxShadow: "0 0 6px rgba(209,126,192,0.7)" }}
          />
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 glass-strong border-white/[0.08]" data-testid="persona-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.16em] text-white/40">
          Switch persona
        </DropdownMenuLabel>
        {PERSONA_ORDER.map((k) => {
          const p = PERSONAS[k];
          const active = k === persona;
          return (
            <DropdownMenuItem
              key={k}
              data-testid={`persona-menu-${k}`}
              onClick={() => setPersona(k)}
              className="cursor-pointer text-xs gap-2"
            >
              <PersonaIcon name={p.icon} className="w-3.5 h-3.5" />
              <span className="flex-1">{p.name}</span>
              {active && <Check className="w-3.5 h-3.5 text-teal-300" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="persona-menu-profile" onClick={() => navigate("/profile")} className="text-xs cursor-pointer">
          <User className="w-3.5 h-3.5 mr-2" /> Profile
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="persona-menu-reset" onClick={reopenSplash} className="text-xs cursor-pointer">
          Choose persona…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------- Persona Banner (venture overview) ----------------
export function PersonaBanner({ doc }) {
  const { persona, config } = usePersona();
  const navigate = useNavigate();
  const callouts = useMemo(() => bannerCallouts(persona, doc), [persona, doc]);
  const ventureId = doc?.run_id;

  const onClick = (c) => {
    if (c.href) navigate(c.href);
    else if (c.to && ventureId) navigate(`/projects/${ventureId}/${c.to}`);
  };

  return (
    <motion.div
      key={persona}
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      data-testid={`persona-banner-${persona}`}
      className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/[0.02] border-l-2"
      style={{ borderLeftColor: config.accent, borderTop: "1px solid rgba(255,255,255,0.04)", borderRight: "1px solid rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
    >
      <div className="flex items-center gap-2 shrink-0">
        <div className="w-7 h-7 rounded-md flex items-center justify-center"
          style={{ background: `rgba(${config.accentRgb}, 0.12)`, border: `1px solid rgba(${config.accentRgb}, 0.35)` }}>
          <PersonaIcon name={config.icon} className="w-3.5 h-3.5" style={{ color: config.accent }} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: config.accent }}>
          {config.bannerLabel}
        </div>
      </div>
      <div className="flex-1 flex flex-wrap items-center gap-2 justify-end">
        {callouts.map((c, i) => (
          <button
            key={i}
            data-testid={`banner-callout-${i}`}
            onClick={() => onClick(c)}
            disabled={!c.to && !c.href}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition flex items-center gap-1.5 ${
              (c.to || c.href) ? "hover:bg-white/[0.05] cursor-pointer" : "cursor-default"
            } ${c.tone === "warn"
              ? "border-amber-400/30 bg-amber-400/[0.05] text-amber-100"
              : "border-white/[0.08] bg-white/[0.02] text-white/75"
            }`}
          >
            <span className="truncate max-w-[260px]">{c.label}</span>
            {c.arrow && <ArrowRight className="w-3 h-3 opacity-60" />}
          </button>
        ))}
      </div>
    </motion.div>
  );
}

// ---------------- TRL Card ----------------
export function TRLCard({ doc }) {
  const { trl, milestone } = useMemo(() => computeTRL(doc), [doc]);
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div data-testid="trl-card" className="rounded-md border border-white/[0.06] bg-white/[0.015] p-3 flex items-center gap-3 cursor-help">
            <div className="text-3xl mono font-semibold text-teal-200 leading-none">{trl}</div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">TRL</div>
              <div className="text-[10px] text-white/40 mt-0.5">Next milestone:</div>
              <div className="text-xs text-white/85 leading-tight line-clamp-2">{milestone}</div>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs">
          Technology Readiness Level — DOD/NASA standard scale 1-9. Higher = closer to market.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------- Priority Chips strip (Command Center) ----------------
export function PriorityChips() {
  const { config } = usePersona();
  const navigate = useNavigate();
  const FEATURED = "fc526bce-d199-433b-8475-8ba28829527f";

  const go = (c) => {
    if (c.soon) {
      toast.success("Added to waitlist — we'll be in touch.");
      return;
    }
    if (c.to) navigate(c.to);
    else if (c.venture) navigate(`/projects/${FEATURED}/${c.venture}`);
  };

  return (
    <div data-testid="priority-chips" className="flex flex-wrap gap-2 mb-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mr-2 self-center">
        Your priorities
      </div>
      {config.priorityChips.map((c, i) => (
        <button
          key={i}
          data-testid={`priority-chip-${i}`}
          onClick={() => go(c)}
          className={`text-[11px] px-2.5 py-1 rounded-full border transition inline-flex items-center gap-1.5 ${
            c.soon
              ? "border-white/[0.08] bg-white/[0.015] text-white/40 hover:border-white/15"
              : "border-teal-400/25 bg-teal-400/[0.06] text-teal-100 hover:bg-teal-400/[0.12]"
          }`}
        >
          {c.soon && <Lock className="w-2.5 h-2.5 opacity-70" />}
          {c.label}
          {c.soon && <span className="text-[9px] uppercase tracking-wider px-1 py-0 rounded bg-white/[0.06] text-white/40">Soon</span>}
        </button>
      ))}
    </div>
  );
}

// ---------------- Founder-only roadmap card ----------------
export function VentureProgressionCard() {
  return (
    <div
      data-testid="venture-progression-card"
      className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 flex flex-col gap-2"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80">Coming Soon</div>
        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.06] text-white/50 border border-white/[0.08]">Beta</span>
      </div>
      <div className="text-sm font-semibold tracking-tight">Venture Progression Engine</div>
      <div className="text-[11px] text-white/55 leading-relaxed">
        Match your venture to grants, incubators, and investors based on TRL, geography, and therapeutic area.
      </div>
      <Button
        size="sm"
        variant="outline"
        data-testid="vpe-waitlist"
        onClick={() => toast.success("Added to waitlist — we'll be in touch.")}
        className="self-start mt-1 border-teal-400/30 text-teal-100 hover:bg-teal-400/10"
      >
        Join waitlist
      </Button>
    </div>
  );
}

// ---------------- Profile Page ----------------
export function ProfilePage() {
  const { persona, config, setPersona } = usePersona();
  const navigate = useNavigate();
  const { runs } = useRunsList();
  const SEPSIS = "fc526bce-d199-433b-8475-8ba28829527f";
  const featured = runs.find((r) => r.run_id === SEPSIS);
  const amr = runs.find((r) => /amr|antimicrob|antibiot|resistance|zonesight|resistwise/i.test((r.problem || "") + (r.company_name || "")));

  const portfolioTRL = [
    featured ? "Sepsis TRL 3" : null,
    amr ? "AMR TRL 3" : null,
  ].filter(Boolean).join(" · ") || "—";

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto" data-testid="profile-page">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ background: `rgba(${config.accentRgb}, 0.12)`, border: `1px solid rgba(${config.accentRgb}, 0.35)` }}>
          <PersonaIcon name={config.icon} className="w-7 h-7" style={{ color: config.accent }} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: config.accent }}>{config.bannerLabel}</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-0.5">{config.name}</h1>
          <div className="text-sm text-white/55">{config.tagline}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-7" data-testid="profile-stats">
        <StatTile label="Ventures Created" value={runs.length} sub="all statuses" />
        <StatTile label="Current TRL Portfolio" value={portfolioTRL.includes("·") ? portfolioTRL : (portfolioTRL || "—")} sub="" small />
        <StatTile label="Grants Matched" value="0" sub="Coming Soon" muted />
        <StatTile label="Incubators Matched" value="0" sub="Coming Soon" muted />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section data-testid="profile-recent">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">Recent ventures</div>
          <div className="glass rounded-xl overflow-hidden border-white/[0.06]">
            {runs.slice(0, 5).map((r) => (
              <button key={r.run_id} onClick={() => navigate(`/projects/${r.run_id}/overview`)}
                className="w-full grid grid-cols-12 items-center px-4 py-2.5 text-sm border-b border-white/[0.04] hover:bg-white/[0.025] text-left">
                <div className="col-span-5 truncate font-medium">{r.company_name || <span className="text-white/30 italic">—</span>}</div>
                <div className="col-span-5 truncate text-white/55 text-xs">{r.problem}</div>
                <div className="col-span-1 text-right mono text-teal-200 text-xs">{r.scores?.innovation_score?.score ?? "—"}</div>
                <div className="col-span-1 text-right text-[10px] text-white/40">{r.status}</div>
              </button>
            ))}
            {runs.length === 0 && <div className="p-4 text-sm text-white/40 italic">No ventures yet.</div>}
          </div>
        </section>

        <section data-testid="profile-switch">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">Switch persona</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PERSONA_ORDER.map((k) => {
              const p = PERSONAS[k];
              const active = k === persona;
              return (
                <button key={k}
                  data-testid={`profile-persona-${k}`}
                  onClick={() => setPersona(k)}
                  className={`rounded-lg p-3 text-left transition border ${active ? "" : "border-white/[0.08] hover:bg-white/[0.03]"}`}
                  style={active ? { borderColor: p.accent, background: `rgba(${p.accentRgb}, 0.08)` } : {}}>
                  <div className="flex items-center gap-2">
                    <PersonaIcon name={p.icon} className="w-3.5 h-3.5" style={{ color: p.accent }} />
                    <div className="text-xs font-semibold">{p.name}</div>
                    {active && <Check className="w-3 h-3 text-teal-300 ml-auto" />}
                  </div>
                  <div className="text-[10px] text-white/45 mt-1 line-clamp-1">{p.tagline}</div>
                </button>
              );
            })}
          </div>
        </section>
      </div>

      <div className="mt-8 pt-4 border-t border-white/[0.04] text-[10px] uppercase tracking-[0.18em] text-white/30 text-center">
        Aignoz Innovation OS · Personalized for healthcare innovation
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, muted, small }) {
  return (
    <div className={`rounded-md border border-white/[0.06] bg-white/[0.015] p-3 ${muted ? "opacity-70" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
      <div className={`${small ? "text-sm" : "text-2xl"} font-semibold mt-1 mono`}>{value}</div>
      {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
    </div>
  );
}
